/**
 * "This part happens in YOUR system" — Plan C goal #6, rendered.
 *
 * Some steps of an integration cannot happen on this screen and never
 * will: creating a Teams Workflow, approving a Slack app, switching on
 * authenticated SMTP in Exchange, generating an access key in a cloud
 * console. No platform can do those, so the honest product decision is
 * to say exactly where the menu is and what to bring back — rather than
 * a button that fails, or silence that turns into a support ticket.
 *
 * Every word here arrives from the server (`GET /integrations/catalog`,
 * already localised). This component decides layout only. That is
 * deliberate: the numbered steps exist once, in
 * `builder/integrations_catalog.py`, so there is no second copy to
 * forget when Microsoft renames a menu item.
 *
 * The one exception is Teams, whose guide already shipped as its own
 * component in Plan C fase 5 with its strings in the i18n dictionaries.
 * The server sends `guide_component: "teams_workflow"` and the caller
 * renders that instead — reusing it rather than writing a second one.
 */

import { AlertTriangle, ExternalLink, MapPin } from "lucide-react";

import { useT } from "@/i18n";
import type { ExternalGuide } from "@/lib/integrations-api";

export interface ExternalGuidePanelProps {
  guide: ExternalGuide;
}

export function ExternalGuidePanel({
  guide,
}: ExternalGuidePanelProps): JSX.Element {
  const { t } = useT();
  return (
    <section
      data-testid="external-guide"
      className="mt-3 rounded-lg border border-neutral-200 bg-neutral-50 p-4 text-sm"
    >
      <header className="flex flex-wrap items-center gap-2">
        <MapPin size={14} className="shrink-0 text-neutral-500" />
        <h4 className="font-semibold text-neutral-900">
          {t("integrations.guide.title")}
        </h4>
        <span className="rounded-full border border-neutral-300 bg-white px-2 py-0.5 text-xs text-neutral-600">
          {guide.where}
        </span>
      </header>

      <p className="mt-2 text-neutral-700">{guide.intro}</p>

      {/* The steps arrive already numbered by the server, so this is a
          plain list — a <ol> would print a second, disagreeing number. */}
      <ul className="mt-3 space-y-1.5 text-neutral-700">
        {guide.steps.map((step) => (
          <li key={step} className="leading-snug">
            {step}
          </li>
        ))}
      </ul>

      {guide.warnings.length > 0 && (
        <>
          <h5 className="mt-4 font-medium text-neutral-800">
            {t("integrations.guide.warnings")}
          </h5>
          <ul className="mt-1 space-y-1 text-neutral-700">
            {guide.warnings.map((warning) => (
              <li key={warning} className="flex items-start gap-2 leading-snug">
                <AlertTriangle size={13} className="mt-0.5 shrink-0" />
                <span>{warning}</span>
              </li>
            ))}
          </ul>
        </>
      )}

      {guide.doc_url !== "" && (
        <a
          className="mt-3 inline-flex items-center gap-1 text-xs text-accent underline"
          href={guide.doc_url}
          target="_blank"
          rel="noreferrer noopener"
        >
          {t("integrations.guide.docsLink")}
          <ExternalLink size={12} />
        </a>
      )}
    </section>
  );
}

export default ExternalGuidePanel;
